import {
  Alert,
  Button,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Modal,
  Select,
  Spin,
  message,
} from 'antd'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import CatchBoardDealActivityTimeline from './CatchBoardDealActivityTimeline'
import CatchBoardDealBuyersSection from './CatchBoardDealBuyersSection'
import CatchBoardDealClosedFieldsSection from './CatchBoardDealClosedFieldsSection'
import CatchBoardDealContextPanel from './CatchBoardDealContextPanel'
import CatchBoardDealNotesSection from './CatchBoardDealNotesSection'
import CatchBoardDealReadinessSection from './CatchBoardDealReadinessSection'
import CatchBoardDealVenuesSection from './CatchBoardDealVenuesSection'
import {
  CATCH_BOARD_COMPANY_NAME_MAX_LENGTH,
  CATCH_BOARD_DEAL_NAME_MAX_LENGTH,
  CATCH_BOARD_DESCRIPTION_MAX_LENGTH,
  CATCH_BOARD_LEAD_SOURCES,
  CATCH_BOARD_LEAD_SOURCE_DETAIL_MAX_LENGTH,
  CATCH_BOARD_PRIORITIES,
  getCatchBoardLeadSourceLabel,
  getCatchBoardPriorityLabel,
  getCatchBoardStageMeta,
} from './catchBoardConstants'
import {
  createCatchBoardDeal,
  fetchCatchBoardAssignees,
  fetchCatchBoardDeal,
  fetchCatchBoardDuplicateVenueWarnings,
  updateCatchBoardDeal,
} from 'src/utils/catchBoardApi'
import {
  buildCatchBoardOwnerOptions,
  filterCatchBoardOwnerOption,
} from 'src/utils/catchBoardOwnerOptions'
import {
  buildCatchBoardDealWriteBody,
  buildEmptyCatchBoardDealForm,
  catchBoardDealToFormValues,
  fromCatchBoardDatePickerValue,
  getCatchBoardCoreStageOptions,
  getCatchBoardDealFormValidation,
  toCatchBoardDatePickerValue,
} from 'src/utils/catchBoardFormHelpers'
import {
  fireCatchBoardLandedConfetti,
  shouldCelebrateCatchBoardLanded,
} from 'src/utils/catchBoardCelebration'
import {
  formatCatchBoardDuplicateVenueAlsoOn,
  hasCatchBoardDuplicateVenueWarnings,
} from 'src/utils/catchBoardDuplicateVenues'
import { mapCatchBoardDeal } from 'src/utils/catchBoardMappers'
import { copyTextToClipboard } from 'src/utils/spartacusClipboard'
import {
  BRIDGE_MINI_TAB_CATCH_BOARD,
  buildBridgeAdminPath,
} from 'src/constants/bridge'
import styled from 'styled-components/macro'

const { TextArea } = Input
const { Option } = Select

/**
 * Create / edit Catch Board deal — core fields, venues, buyers, won/lost.
 */
const CatchBoardDealModal = ({
  visible,
  dealId = null,
  ownerNameHint = '',
  primaryVenueNameHint = '',
  createDraft = null,
  onClose,
  onSaved,
}) => {
  const account = useSelector(state => state.auth?.account)
  const accountId = account?._id || null
  const accountLabel =
    account?.name ||
    account?.emailAddress ||
    account?.email ||
    'Me'

  const isCreate = !dealId
  const [form, setForm] = useState(() =>
    buildEmptyCatchBoardDealForm({ accountId }),
  )
  const [originalStage, setOriginalStage] = useState(null)
  const [ownerNameById, setOwnerNameById] = useState({})
  const [assignees, setAssignees] = useState([])
  const [loading, setLoading] = useState(false)
  const [loadError, setLoadError] = useState(null)
  const [submitting, setSubmitting] = useState(false)
  const [saveError, setSaveError] = useState(null)
  const [fieldErrors, setFieldErrors] = useState({})
  const [notesRefreshNonce, setNotesRefreshNonce] = useState(0)
  const [activityLog, setActivityLog] = useState([])
  const closedFieldsRef = useRef(null)

  useEffect(() => {
    if (!visible) {
      return undefined
    }

    let cancelled = false

    async function loadAssignees() {
      try {
        const rows = await fetchCatchBoardAssignees()
        if (!cancelled) {
          setAssignees(Array.isArray(rows) ? rows : [])
        }
      } catch {
        if (!cancelled) {
          setAssignees([])
        }
      }
    }

    loadAssignees()

    return () => {
      cancelled = true
    }
  }, [visible])

  useEffect(() => {
    if (!visible) {
      return undefined
    }

    let cancelled = false

    async function hydrate() {
      setLoadError(null)
      setSaveError(null)
      setFieldErrors({})

      if (isCreate) {
        const empty = buildEmptyCatchBoardDealForm({ accountId })
        setForm(
          createDraft
            ? {
                ...empty,
                ...createDraft,
                ownerAccountId:
                  createDraft.ownerAccountId ?? empty.ownerAccountId,
              }
            : empty,
        )
        setOriginalStage(null)
        setActivityLog([])
        setOwnerNameById(
          accountId ? { [String(accountId)]: accountLabel } : {},
        )
        setLoading(false)
        return
      }

      setLoading(true)
      try {
        const raw = await fetchCatchBoardDeal(dealId)
        if (cancelled) {
          return
        }

        const deal = mapCatchBoardDeal(raw || {})
        setForm(
          catchBoardDealToFormValues(deal, { primaryVenueNameHint }),
        )
        setOriginalStage(deal.stage || null)
        setActivityLog(deal.activityLog || [])

        const names = {}
        if (accountId) {
          names[String(accountId)] = accountLabel
        }
        if (deal.ownerAccountId) {
          names[String(deal.ownerAccountId)] =
            deal.ownerName ||
            ownerNameHint ||
            names[String(deal.ownerAccountId)] ||
            'Deal owner'
        }
        setOwnerNameById(names)
      } catch (err) {
        if (!cancelled) {
          setLoadError(err.message || 'Failed to load deal')
        }
      } finally {
        if (!cancelled) {
          setLoading(false)
        }
      }
    }

    hydrate()

    return () => {
      cancelled = true
    }
  }, [visible, dealId, isCreate, accountId, accountLabel, ownerNameHint, primaryVenueNameHint, createDraft])

  const stageOptions = useMemo(
    () => getCatchBoardCoreStageOptions(),
    [],
  )

  const ownerOptions = useMemo(
    () =>
      buildCatchBoardOwnerOptions(assignees, {
        accountId,
        accountLabel,
        currentOwnerAccountId: form.ownerAccountId,
        currentOwnerLabel:
          (form.ownerAccountId &&
            ownerNameById[String(form.ownerAccountId)]) ||
          '',
      }),
    [
      assignees,
      accountId,
      accountLabel,
      form.ownerAccountId,
      ownerNameById,
    ],
  )

  const handleFieldChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }))
    setFieldErrors(prev => {
      if (!prev || !Object.keys(prev).length) {
        return prev
      }
      if (field === 'stage') {
        return {}
      }
      if (!prev[field]) {
        return prev
      }
      const next = { ...prev }
      delete next[field]
      return next
    })
  }

  const handleFormPatch = nextForm => {
    setForm(nextForm)
  }

  const scrollToClosedFields = () => {
    const node = closedFieldsRef.current
    if (!node || typeof node.scrollIntoView !== 'function') {
      return
    }
    node.scrollIntoView({ behavior: 'smooth', block: 'center' })
  }

  const handleNoteCreated = async () => {
    setNotesRefreshNonce(value => value + 1)
    if (!dealId) {
      return
    }
    try {
      const raw = await fetchCatchBoardDeal(dealId)
      const deal = mapCatchBoardDeal(raw || {})
      setActivityLog(deal.activityLog || [])
    } catch {
      // Note already saved; activity can stay stale until next open.
    }
  }

  const handleApplyDealFollowUpFromNote = async followUpMoment => {
    if (!dealId || !followUpMoment) {
      return
    }

    const nextFollowUpAt = fromCatchBoardDatePickerValue(followUpMoment)
    try {
      const saved = await updateCatchBoardDeal(dealId, { nextFollowUpAt })
      const mapped = mapCatchBoardDeal(saved || {})
      setForm(prev => ({
        ...prev,
        nextFollowUpAt:
          toCatchBoardDatePickerValue(mapped.nextFollowUpAt) || followUpMoment,
      }))
      if (Array.isArray(mapped.activityLog)) {
        setActivityLog(mapped.activityLog)
      }
      message.success('Deal follow-up updated')
      onSaved?.(mapped)
    } catch (err) {
      message.error(err.message || 'Failed to update deal follow-up')
      throw err
    }
  }

  const handleSubmit = async () => {
    const validation = getCatchBoardDealFormValidation(form)
    if (validation) {
      setFieldErrors(validation.fieldErrors || {})
      if (validation.scrollTo === 'closed-fields') {
        // Let the closed fields section paint inline errors first.
        window.requestAnimationFrame(() => scrollToClosedFields())
      }
      message.error(validation.message)
      return
    }

    setFieldErrors({})

    const includeStage =
      isCreate || (form.stage && form.stage !== originalStage)
    const body = buildCatchBoardDealWriteBody(form, { includeStage })

    setSubmitting(true)
    setSaveError(null)
    try {
      const linkedVenueIds = (form.linkedVenues || [])
        .map(venue => venue?.venueId)
        .filter(Boolean)
      if (linkedVenueIds.length) {
        try {
          const duplicateWarnings = await fetchCatchBoardDuplicateVenueWarnings(
            {
              venueIds: linkedVenueIds,
              excludeDealId: dealId,
            },
          )
          if (hasCatchBoardDuplicateVenueWarnings(duplicateWarnings)) {
            const lines = duplicateWarnings.map(warning => {
              const venueName =
                (form.linkedVenues || []).find(
                  venue => String(venue.venueId) === String(warning.venueId),
                )?.name || `Venue ${warning.venueId}`
              return `${venueName}: ${formatCatchBoardDuplicateVenueAlsoOn(
                warning,
              )}`
            })
            message.warning(lines.join(' · '))
          }
        } catch {
          // Warn-only — save still proceeds if duplicate check fails.
        }
      }

      const saved = isCreate
        ? await createCatchBoardDeal(body)
        : await updateCatchBoardDeal(dealId, body)

      if (shouldCelebrateCatchBoardLanded(originalStage, form.stage)) {
        fireCatchBoardLandedConfetti()
      }

      message.success(
        isCreate
          ? form.stage === 'landed'
            ? 'Deal landed'
            : 'Deal created'
          : form.stage === 'landed' && originalStage !== 'landed'
            ? 'Deal landed'
            : 'Deal updated',
      )
      onSaved?.(mapCatchBoardDeal(saved || {}))
      onClose?.()
    } catch (err) {
      const msg = err.message || 'Failed to save deal'
      setSaveError(msg)
      message.error(msg)
    } finally {
      setSubmitting(false)
    }
  }

  const handleCopyLink = async () => {
    if (!dealId) {
      return
    }
    try {
      const path = buildBridgeAdminPath(BRIDGE_MINI_TAB_CATCH_BOARD, { dealId })
      const url = `${window.location.origin}${path}`
      await copyTextToClipboard(url)
      message.success('Link copied')
    } catch (err) {
      message.error(err?.message || 'Could not copy link')
    }
  }

  const showForm = !loading && !loadError

  const primaryVenue = useMemo(() => {
    const primaryId = form.primaryVenueId ? String(form.primaryVenueId) : null
    if (!primaryId) {
      return null
    }
    return (
      (form.linkedVenues || []).find(
        venue => String(venue.venueId) === primaryId,
      ) || { venueId: primaryId, name: primaryVenueNameHint || '' }
    )
  }, [form.linkedVenues, form.primaryVenueId, primaryVenueNameHint])

  return (
    <StyledModal
      visible={visible}
      title={isCreate ? 'New deal' : 'Edit deal'}
      width={isCreate ? 640 : 720}
      onCancel={onClose}
      destroyOnClose
      maskClosable={!submitting}
      footer={
        loadError
          ? [
              <Button key='close' onClick={onClose}>
                Close
              </Button>,
            ]
          : [
              !isCreate && dealId ? (
                <Button
                  key='copy-link'
                  onClick={handleCopyLink}
                  disabled={submitting}
                  data-testid='catch-board-deal-copy-link'
                >
                  Copy link
                </Button>
              ) : null,
              <Button key='cancel' onClick={onClose} disabled={submitting}>
                Cancel
              </Button>,
              <Button
                key='save'
                type='primary'
                loading={submitting}
                disabled={loading}
                onClick={handleSubmit}
                data-testid='catch-board-deal-save'
              >
                {isCreate ? 'Create deal' : 'Save changes'}
              </Button>,
            ].filter(Boolean)
      }
    >
      {loading ? (
        <LoadingWrap data-testid='catch-board-deal-loading'>
          <Spin />
          <span>Loading deal…</span>
        </LoadingWrap>
      ) : null}

      {loadError ? (
        <Alert type='error' showIcon message={loadError} />
      ) : null}

      {showForm ? (
        <FormLayout data-testid='catch-board-deal-form'>
          {saveError ? (
            <Alert
              type='error'
              showIcon
              message={saveError}
              style={{ marginBottom: 12 }}
            />
          ) : null}

          <Form.Item label='Deal name' required style={{ marginBottom: 12 }}>
            <Input
              value={form.name}
              maxLength={CATCH_BOARD_DEAL_NAME_MAX_LENGTH}
              placeholder='e.g. Marriott rollout'
              onChange={event => handleFieldChange('name', event.target.value)}
              data-testid='catch-board-deal-name'
            />
          </Form.Item>

          <Form.Item label='Company' style={{ marginBottom: 12 }}>
            <Input
              value={form.companyName}
              maxLength={CATCH_BOARD_COMPANY_NAME_MAX_LENGTH}
              placeholder='Company or group name'
              onChange={event =>
                handleFieldChange('companyName', event.target.value)
              }
              data-testid='catch-board-deal-company'
            />
          </Form.Item>

          <RowFields>
            <Form.Item label='Stage' style={{ marginBottom: 12, flex: 1 }}>
              <Select
                style={{ width: '100%' }}
                dropdownMatchSelectWidth={false}
                dropdownStyle={{ minWidth: 180 }}
                value={form.stage}
                onChange={value => handleFieldChange('stage', value)}
                data-testid='catch-board-deal-stage'
              >
                {stageOptions.map(stage => {
                  const meta = getCatchBoardStageMeta(stage)
                  return (
                    <Option key={stage} value={stage}>
                      {meta?.emoji ? `${meta.emoji} ` : ''}
                      {meta?.label || stage}
                    </Option>
                  )
                })}
              </Select>
            </Form.Item>

            <Form.Item label='Priority' style={{ marginBottom: 12, flex: 1 }}>
              <Select
                style={{ width: '100%' }}
                dropdownMatchSelectWidth={false}
                dropdownStyle={{ minWidth: 140 }}
                value={form.priority}
                onChange={value => handleFieldChange('priority', value)}
                data-testid='catch-board-deal-priority'
              >
                {CATCH_BOARD_PRIORITIES.map(priority => (
                  <Option key={priority} value={priority}>
                    {getCatchBoardPriorityLabel(priority)}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </RowFields>

          <CatchBoardDealReadinessSection
            form={form}
            onFieldChange={handleFieldChange}
            disabled={submitting}
          />

          <CatchBoardDealClosedFieldsSection
            ref={closedFieldsRef}
            form={form}
            onFieldChange={handleFieldChange}
            disabled={submitting}
            fieldErrors={fieldErrors}
          />

          <RowFields>
            <Form.Item
              label='Deal value (£)'
              style={{ marginBottom: 12, flex: 1 }}
            >
              <InputNumber
                style={{ width: '100%' }}
                min={0}
                step={100}
                precision={2}
                value={form.dealValuePounds}
                onChange={value =>
                  handleFieldChange(
                    'dealValuePounds',
                    value == null ? null : value,
                  )
                }
                data-testid='catch-board-deal-value'
              />
            </Form.Item>

            <Form.Item
              label='Potential value (£)'
              style={{ marginBottom: 12, flex: 1 }}
            >
              <InputNumber
                style={{ width: '100%' }}
                min={0}
                step={100}
                precision={2}
                value={form.potentialDealValuePounds}
                onChange={value =>
                  handleFieldChange(
                    'potentialDealValuePounds',
                    value == null ? null : value,
                  )
                }
                data-testid='catch-board-deal-potential-value'
              />
            </Form.Item>
          </RowFields>

          <Form.Item label='Owner' style={{ marginBottom: 12 }}>
            <Select
              style={{ width: '100%' }}
              dropdownMatchSelectWidth={false}
              dropdownStyle={{ minWidth: 200 }}
              value={form.ownerAccountId || ''}
              allowClear
              showSearch
              // antd 3: Option children + filter by label text (not value ids).
              optionFilterProp='children'
              filterOption={filterCatchBoardOwnerOption}
              placeholder='Unassigned'
              onChange={value =>
                handleFieldChange('ownerAccountId', value || null)
              }
              data-testid='catch-board-deal-owner'
            >
              {ownerOptions.map(option => (
                <Option
                  key={option.value === '' ? 'unassigned' : option.value}
                  value={option.value}
                >
                  {option.label}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <RowFields>
            <Form.Item
              label='Lead source'
              style={{ marginBottom: 12, flex: 1 }}
            >
              <Select
                style={{ width: '100%' }}
                dropdownMatchSelectWidth={false}
                dropdownStyle={{ minWidth: 220 }}
                value={form.leadSource}
                allowClear
                placeholder='Select source'
                onChange={value =>
                  handleFieldChange('leadSource', value || null)
                }
                data-testid='catch-board-deal-lead-source'
              >
                {CATCH_BOARD_LEAD_SOURCES.map(source => (
                  <Option key={source} value={source}>
                    {getCatchBoardLeadSourceLabel(source)}
                  </Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item
              label='Source detail'
              style={{ marginBottom: 12, flex: 1 }}
            >
              <Input
                value={form.leadSourceDetail}
                maxLength={CATCH_BOARD_LEAD_SOURCE_DETAIL_MAX_LENGTH}
                placeholder='Optional detail'
                disabled={!form.leadSource}
                onChange={event =>
                  handleFieldChange('leadSourceDetail', event.target.value)
                }
                data-testid='catch-board-deal-lead-source-detail'
              />
            </Form.Item>
          </RowFields>

          <RowFields>
            <Form.Item
              label='Expected close'
              style={{ marginBottom: 12, flex: 1 }}
            >
              <DatePicker
                style={{ width: '100%' }}
                value={form.expectedCloseDate}
                allowClear
                format='D MMM YYYY'
                onChange={value =>
                  handleFieldChange('expectedCloseDate', value || null)
                }
                data-testid='catch-board-deal-expected-close'
              />
            </Form.Item>

            <Form.Item
              label='Next follow-up'
              style={{ marginBottom: 12, flex: 1 }}
            >
              <DatePicker
                style={{ width: '100%' }}
                value={form.nextFollowUpAt}
                allowClear
                format='D MMM YYYY'
                onChange={value =>
                  handleFieldChange('nextFollowUpAt', value || null)
                }
                data-testid='catch-board-deal-next-follow-up'
              />
            </Form.Item>
          </RowFields>

          <Form.Item label='Description' style={{ marginBottom: 12 }}>
            <TextArea
              rows={4}
              value={form.description}
              maxLength={CATCH_BOARD_DESCRIPTION_MAX_LENGTH}
              placeholder='Context for the team…'
              onChange={event =>
                handleFieldChange('description', event.target.value)
              }
              data-testid='catch-board-deal-description'
            />
          </Form.Item>

          <CatchBoardDealVenuesSection
            form={form}
            onFormChange={handleFormPatch}
            disabled={submitting}
            excludeDealId={dealId}
            enabled={visible && showForm}
          />

          <CatchBoardDealBuyersSection
            form={form}
            onFormChange={handleFormPatch}
            disabled={submitting}
          />

          {!isCreate ? (
            <CatchBoardDealNotesSection
              primaryVenueId={primaryVenue?.venueId || null}
              primaryVenueName={primaryVenue?.name || ''}
              disabled={submitting}
              onNoteCreated={handleNoteCreated}
              onApplyDealFollowUp={handleApplyDealFollowUpFromNote}
            />
          ) : null}

          {!isCreate ? (
            <CatchBoardDealContextPanel
              dealId={dealId}
              linkedVenues={form.linkedVenues}
              primaryVenueId={form.primaryVenueId}
              enabled={visible && showForm}
              refreshNonce={notesRefreshNonce}
            />
          ) : null}

          {!isCreate ? (
            <CatchBoardDealActivityTimeline activityLog={activityLog} />
          ) : null}
        </FormLayout>
      ) : null}
    </StyledModal>
  )
}

const StyledModal = styled(Modal)`
  .ant-modal-body {
    padding-top: 12px;
  }
`

const FormLayout = styled.div`
  display: flex;
  flex-direction: column;
`

const RowFields = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0 12px;

  > * {
    min-width: 180px;
  }
`

const LoadingWrap = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-height: 8rem;
  color: ${({ theme }) => theme.colours.stormGrey || '#666'};
`

export default CatchBoardDealModal
